import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  Stack,
  Paper,
  useTheme,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogContent,
  Skeleton,
  Tooltip,
  Badge,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  PhotoCamera as PhotoIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  LocationOn as LocationIcon,
  BrokenImage as BrokenImageIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Engineering as EngineeringIcon,
  Build as BuildIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { dailyReportsAPI } from '../../services/dailyReportsAPI';
import { useSnackbar } from 'notistack';
import { api } from '../../services/api';
import { DailyReport, AnwesenderEintrag } from '../../types/api';
import { API_BASE_URL } from '../../config';

// Helper function to construct photo URL
const getPhotoUrl = (photo: any): string => {
  if (!photo || !photo.file_path) return '';
  
  // Remove leading 'static/' if present since it's included in the API path
  const path = photo.file_path.replace(/^static\//, '');
  
  // Construct the full URL - note we need to go up one level since photos are served from /static
  return `${API_BASE_URL.replace(/\/api$/, '')}/static/${path}`;
};

const DailyReportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  
  const [report, setReport] = useState<DailyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(-1);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);

  // Rollen-basierte Berechtigungen
  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management';
  const isBauleiter = user?.role === 'bauleiter';
  const isForeman = user?.role === 'foreman';
  const isInstaller = user?.role === 'installer';
  
  // Admin, Management & Bauleiter können alles
  const canSeeAll = isAdmin || isManagement || isBauleiter;
  // Vorarbeiter & Monteure haben eingeschränkte Rechte
  const canSeeRestricted = isForeman || isInstaller;
  
  // Export-Berechtigung: Nur Admin, Management & Bauleiter
  const canExportReports = canSeeAll;
  
  // Bearbeiten-Berechtigung: Admin, Management & Bauleiter können alles bearbeiten
  // Vorarbeiter & Monteure können nur eigene Entwürfe bearbeiten
  const canEditReport = (report: DailyReport | null) => {
    if (!report) return false;
    
    // Admin, Management & Bauleiter können alle Berichte bearbeiten
    if (canSeeAll) return true;
    
    // Vorarbeiter & Monteure können nur ihre eigenen Berichte bearbeiten
    // und nur wenn Status = "entwurf"
    if (canSeeRestricted) {
      const isOwnReport = report.created_by === user?.id || 
                         report.ersteller === user?.email;
      const isDraft = report.status === 'entwurf';
      return isOwnReport && isDraft;
    }
    
    return false;
  };

  useEffect(() => {
    const fetchReport = async () => {
      try {
        if (!id) return;
        const data = await dailyReportsAPI.getReport(Number(id));
        setReport(data);
      } catch (error) {
        console.error('Error fetching report:', error);
        enqueueSnackbar('Fehler beim Laden des Tagesberichts', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [id, enqueueSnackbar]);

  const buildPdfFilename = (reportData: DailyReport | null) => {
    const siteRaw = (reportData?.ort || reportData?.projekt || 'Unbekannt')
      .replace(/[^\wÄÖÜäöüß\-]+/gi, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
    const d = reportData?.date ? new Date(reportData.date) : new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `Tagesbericht_${siteRaw || 'Unbekannt'}_${dd}.${mm}.${yy}.pdf`;
  };

  const handlePdfDownload = async () => {
    setPdfLoading(true);
    try {
      const response = await api.get(
        `/daily-reports/${id}/export-pdf`,
        {
          responseType: 'blob',
        }
      );
      const pdfBlob = new Blob([response.data as BlobPart], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers?.['content-disposition'] as string | undefined;
      const headerMatch = disposition?.match(/filename\*?=(?:UTF-8''|")?([^\";]+)/i);
      const filename = headerMatch
        ? decodeURIComponent(headerMatch[1].replace(/"/g, ''))
        : buildPdfFilename(report);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('PDF erfolgreich heruntergeladen', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Fehler beim PDF-Download', { variant: 'error' });
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePhotoClick = (index: number) => {
    setSelectedPhotoIndex(index);
    setShowPhotoDialog(true);
  };

  const handleClosePhotoDialog = () => {
    setShowPhotoDialog(false);
  };

  const handlePrevPhoto = () => {
    const photos = report?.photos;
    if (!photos?.length) return;
    setSelectedPhotoIndex((prev) => 
      prev > 0 ? prev - 1 : photos.length - 1
    );
  };

  const handleNextPhoto = () => {
    const photos = report?.photos;
    if (!photos?.length) return;
    setSelectedPhotoIndex((prev) => 
      prev < photos.length - 1 ? prev + 1 : 0
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!report) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="50vh"
        gap={2}
      >
        <DescriptionIcon color="error" sx={{ fontSize: 48 }} />
        <Typography variant="h6" color="text.secondary">
          Tagesbericht nicht gefunden
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tagesberichte')}
        >
          Zurück zur Übersicht
        </Button>
      </Box>
    );
  }

  const InfoRow = ({ label, value, icon }: { label: string; value: string | number | null; icon?: React.ReactNode }) => (
    <Box sx={{ py: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {icon}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {label}
        </Typography>
      </Stack>
      <Typography variant="body1" sx={{ pl: icon ? 4 : 0 }}>
        {value || '-'}
      </Typography>
    </Box>
  );

  const anwesende: AnwesenderEintrag[] = (() => {
    if (!report.anwesende) return [];
    try {
      return typeof report.anwesende === 'string' ? JSON.parse(report.anwesende) : report.anwesende;
    } catch (error) {
      console.error('Error parsing anwesende:', error, 'Raw value:', report.anwesende);
      return [];
    }
  })();

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title="Zurück zur Übersicht">
            <IconButton 
              onClick={() => navigate('/tagesberichte')} 
              sx={{ 
                mr: 1,
                bgcolor: theme.palette.grey[100],
                '&:hover': {
                  bgcolor: theme.palette.grey[200],
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h4" component="h1">
            Tagesbericht #{report.rapport_nr || report.id}
          </Typography>
        </Box>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
          {canExportReports && (
            <Button
              variant="outlined"
              startIcon={<PdfIcon />}
              onClick={handlePdfDownload}
              disabled={pdfLoading}
              sx={{
                minWidth: isMobile ? '100%' : 'auto',
              }}
            >
              {pdfLoading ? 'Wird heruntergeladen...' : 'PDF Export'}
            </Button>
          )}
          {canEditReport(report) && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/tagesberichte/${id}/edit`)}
              sx={{
                minWidth: isMobile ? '100%' : 'auto',
              }}
            >
              Bearbeiten
            </Button>
          )}
        </Stack>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Tagesbericht Details
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                  <Typography variant="subtitle2">Projekt</Typography>
                  <Typography>{report.projekt || '-'}</Typography>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                  <Typography variant="subtitle2">Berichtnummer</Typography>
                  <Typography>{report.berichtnummer || '-'}</Typography>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                  <Typography variant="subtitle2">Datum</Typography>
                  <Typography>
                    {report.date ? format(new Date(report.date), 'dd.MM.yyyy', { locale: de }) : '-'}
                  </Typography>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                  <Typography variant="subtitle2">Ersteller</Typography>
                  <Typography>{report.ersteller || '-'}</Typography>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                  <Typography variant="subtitle2">Straße</Typography>
                  <Typography>{report.strasse || '-'}</Typography>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                  <Typography variant="subtitle2">Ort</Typography>
                  <Typography>{report.ort || '-'}</Typography>
                </Box>
              </Box>

              <Typography variant="h6" gutterBottom>Witterung</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(33.33% - 8px)' } }}>
                  <Typography variant="subtitle2">Uhrzeit</Typography>
                  <Typography>{report.uhrzeit || '-'}</Typography>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(33.33% - 8px)' } }}>
                  <Typography variant="subtitle2">Bewölkung</Typography>
                  <Typography>{report.bewoelkung || '-'}</Typography>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(33.33% - 8px)' } }}>
                  <Typography variant="subtitle2">Wetterlage</Typography>
                  <Typography>{report.wetterlage || '-'}</Typography>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(33.33% - 8px)' } }}>
                  <Typography variant="subtitle2">Wind</Typography>
                  <Typography>{report.wind || '-'}</Typography>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(33.33% - 8px)' } }}>
                  <Typography variant="subtitle2">Temperatur</Typography>
                  <Typography>{report.temperatur || '-'}</Typography>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(33.33% - 8px)' } }}>
                  <Typography variant="subtitle2">Luftfeuchtigkeit</Typography>
                  <Typography>{report.luftfeuchtigkeit || '-'}</Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>Anwesende Personen</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Firma</TableCell>
                      <TableCell>Personenzahl</TableCell>
                      <TableCell>Tätigkeit</TableCell>
                      <TableCell>Arbeitszeit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {anwesende.length > 0 ? (
                      anwesende.map((person, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{person.firma}</TableCell>
                          <TableCell>{person.personenzahl}</TableCell>
                          <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{person.taetigkeit}</TableCell>
                          <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{person.notiz}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          Keine Einträge vorhanden
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Anmerkungen Sektion */}
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Anmerkungen</Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {report.anmerkungen || 'Keine Anmerkungen vorhanden'}
                  </Typography>
                </Paper>
              </>

              {report.photos && report.photos.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>Fotos</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {report.photos?.map((photo, idx) => {
                      // Remove /api from API_BASE_URL for static files
                      const baseUrl = API_BASE_URL.replace('/api', '');
                      const photoUrl = `${baseUrl}/static/photos/${photo.file_path.split('/').pop()}`;
                      return (
                        <Box key={idx} sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 calc(33.33% - 8px)', md: '1 1 calc(25% - 8px)' } }}>
                          <img
                            src={photoUrl}
                            alt={`Foto ${idx + 1}`}
                            style={{
                              width: '100%',
                              height: 'auto',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              console.error('Error loading image:', photoUrl);
                              const fallbackUrl = `${baseUrl}/static/logo.png`;
                              e.currentTarget.src = fallbackUrl;
                            }}
                            onClick={() => setSelectedPhoto(photoUrl)}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </>
              )}

              {report.documents && report.documents.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>Angehängte Dateien</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {report.documents.map((doc: any, idx: number) => {
                      const baseUrl = API_BASE_URL.replace('/api', '');
                      const docUrl = `${baseUrl}/${doc.file_path}`;
                      return (
                        <Paper 
                          key={idx} 
                          variant="outlined" 
                          sx={{ 
                            p: 2, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2,
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          <DescriptionIcon color="primary" />
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {doc.original_filename}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(doc.file_size / 1024).toFixed(1)} KB
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            href={docUrl}
                            target="_blank"
                            download
                          >
                            Download
                          </Button>
                        </Paper>
                      );
                    })}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Bildvorschau Dialog */}
      <Dialog
        open={!!selectedPhoto}
        onClose={() => setSelectedPhoto(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography>Foto Ansicht</Typography>
            <IconButton onClick={() => setSelectedPhoto(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPhoto && (
            <img
              src={selectedPhoto}
              alt="Vergrößerte Ansicht"
              style={{ width: '100%', height: 'auto' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DailyReportDetail; 